/**
 * Enhanced Button Component with Modern Visual Appeal
 * Gradients, animations, hover effects, and premium styling
 */

import React from 'react';
import { Button as FlowbiteBtn, ButtonProps as FlowbiteBtnProps } from 'flowbite-react';

interface EnhancedButtonProps extends Omit<FlowbiteBtnProps, 'color'> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'gradient' | 'premium';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  pulse?: boolean;
  glow?: boolean;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Modern Enhanced Button with premium styling and animations
 */
const EnhancedButton: React.FC<EnhancedButtonProps> = ({
  variant = 'primary',
  icon,
  iconPosition = 'left',
  pulse = false,
  glow = false,
  rounded = 'lg',
  shadow = 'md',
  children,
  className = '',
  ...props
}) => {
  // Custom styling classes based on variants
  const getVariantClasses = () => {
    const baseClasses = 'relative overflow-hidden transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0';
    
    switch (variant) {
      case 'primary':
        return `${baseClasses} bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold ${glow ? 'shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40' : ''}`;
      
      case 'success':
        return `${baseClasses} bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold ${glow ? 'shadow-lg shadow-green-500/25 hover:shadow-green-500/40' : ''}`;
      
      case 'danger':
        return `${baseClasses} bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-semibold ${glow ? 'shadow-lg shadow-red-500/25 hover:shadow-red-500/40' : ''}`;
      
      case 'warning':
        return `${baseClasses} bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-semibold ${glow ? 'shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40' : ''}`;
      
      case 'gradient':
        return `${baseClasses} bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 hover:from-purple-600 hover:via-pink-600 hover:to-red-600 text-white font-semibold ${glow ? 'shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50' : ''}`;
      
      case 'premium':
        return `${baseClasses} bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 hover:from-amber-600 hover:via-yellow-600 hover:to-orange-600 text-white font-semibold ${glow ? 'shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50' : ''} relative before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700`;
      
      case 'secondary':
        return `${baseClasses} bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium border border-gray-300 hover:border-gray-400`;
      
      case 'light':
        return `${baseClasses} bg-white hover:bg-gray-50 text-gray-700 font-medium border border-gray-200 hover:border-gray-300 ${glow ? 'shadow-md hover:shadow-lg' : ''}`;
      
      default:
        return `${baseClasses} bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold`;
    }
  };

  const getRoundedClasses = () => {
    switch (rounded) {
      case 'none': return 'rounded-none';
      case 'sm': return 'rounded-sm';
      case 'md': return 'rounded-md';
      case 'lg': return 'rounded-lg';
      case 'xl': return 'rounded-xl';
      case 'full': return 'rounded-full';
      default: return 'rounded-lg';
    }
  };

  const getShadowClasses = () => {
    if (glow) return ''; // Glow effect handles shadow
    switch (shadow) {
      case 'none': return 'shadow-none';
      case 'sm': return 'shadow-sm';
      case 'md': return 'shadow-md';
      case 'lg': return 'shadow-lg';
      case 'xl': return 'shadow-xl';
      default: return 'shadow-md';
    }
  };

  const pulseClasses = pulse ? 'animate-pulse' : '';

  // Combine all classes
  const allClasses = [
    getVariantClasses(),
    getRoundedClasses(),
    getShadowClasses(),
    pulseClasses,
    'px-6 py-3 min-h-[44px] focus:outline-none focus:ring-4 focus:ring-opacity-50',
    className
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {icon && iconPosition === 'left' && (
        <span className="mr-2 inline-flex">{icon}</span>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <span className="ml-2 inline-flex">{icon}</span>
      )}
    </>
  );

  // Use a regular button with custom styling instead of Flowbite for maximum control
  return (
    <button
      className={allClasses}
      {...props}
    >
      {content}
      
      {/* Ripple effect for premium buttons */}
      {variant === 'premium' && (
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-1000" />
      )}
    </button>
  );
};

export default EnhancedButton;